import { DialogDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { getSteps, SurveyProgress, type SurveyData } from './survey';
import { Dialog, DialogActions, DialogButton, DialogTitle } from '~/components/ui/LegacyDialog';
import { surveyStore } from '~/lib/stores/survey';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { getFormData } from '~/utils/form';

interface SurveyDialogProps {
  open: boolean;
  allowSkip?: boolean;
  portalContainer?: Element;
}

const MotionDialogActions = motion(DialogActions);

export const SurveyDialog = memo(({ open, portalContainer, allowSkip }: SurveyDialogProps) => {
  const [step, setStep] = useState(0);
  const [animateDirection, setAnimateDirection] = useState(-1);
  const [collectedData, setCollectedData] = useState<SurveyData>({});
  const [isStepValid, setIsStepValid] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [animationCompleted, setAnimationCompleted] = useState(false);

  useEffect(() => {
    const unsubscribe = surveyStore.$surveyData.subscribe((data) => {
      setCollectedData(data.data);
    });

    surveyStore.load();

    return () => {
      unsubscribe();
    };
  }, []);

  const StepComponents = getSteps(collectedData?.role);

  const StepComponent = StepComponents[step];

  const onBack = useCallback(() => {
    if (step === 0) {
      surveyStore.dismissSurvey();
    } else {
      setAnimateDirection(-1);
      setStep(step - 1);
    }
  }, [step]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (step === StepComponents.length - 1) {
        surveyStore.completeSurvey();

        return;
      }

      const data = getFormData(event);

      setAnimateDirection(1);

      const newData = { ...collectedData, ...data };

      setCollectedData(newData);

      setStep(step + 1);

      // we complete the survey at the last step, not at the overview
      const isFinished = step === StepComponents.length - 2;

      surveyStore.updateSurveyData(newData, isFinished);
    },
    [step, collectedData, StepComponents.length],
  );

  const validate = useCallback(() => {
    requestAnimationFrame(() => {
      const isValid = formRef.current?.checkValidity() ?? false;

      setIsStepValid(isValid);
    });
  }, []);

  const isFullScreenStep = useMemo(() => {
    return step === 0 || step === StepComponents.length - 1;
  }, [step, StepComponents.length]);

  useEffect(() => {
    if (animationCompleted) {
      validate();
    }
  }, [animationCompleted, step]);

  const handleClose = useCallback(() => {
    if (step === StepComponents.length - 1) {
      surveyStore.completeSurvey();
    } else {
      surveyStore.dismissSurvey();
    }
  }, [step, StepComponents.length]);

  const dialogAnimationVariants = {
    enter: (direction: number) => {
      return {
        x: direction > 0 ? 576 : -576,
        opacity: 0,
      };
    },
    active: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => {
      return {
        x: direction < 0 ? 576 : -576,
        opacity: 0,
      };
    },
  };

  return (
    <Dialog
      open={open}
      width={576}
      height={694}
      maxWidth="90%"
      maxHeight="90%"
      showCloseButton={allowSkip}
      onClose={handleClose}
      portalContainer={portalContainer}
      closeOnBackdropClick={false}
      modal
      contentClassName="flex flex-col"
    >
      <VisuallyHidden>
        <DialogTitle>Quick Survey: Tell Us About Yourself</DialogTitle>

        <DialogDescription>
          Help us tailor Bolt to your needs and get 1M free tokens for completing this short survey.
        </DialogDescription>
      </VisuallyHidden>

      <form
        className="flex flex-col flex-1 overflow-auto"
        onSubmit={handleSubmit}
        noValidate
        ref={formRef}
        onChange={validate}
      >
        <div
          className="grid [&>*]:[grid-area:1/1/2/2] flex-1 transition-padding duration-500 ease-in-out overflow-hidden"
          style={{
            paddingBottom: step > 1 && step < StepComponents.length - 1 ? 67 : 0,
          }}
        >
          <AnimatePresence initial={false} custom={animateDirection}>
            <StepComponent
              key={step}
              onBack={onBack}
              variants={dialogAnimationVariants}
              initial="enter"
              custom={animateDirection}
              animate="active"
              exit="exit"
              validate={validate}
              allowSkip={allowSkip}
              onAnimationStart={() => setAnimationCompleted(false)}
              onAnimationComplete={() => setAnimationCompleted(true)}
              initialValues={collectedData}
              transition={{ duration: 0.5, ease: cubicEasingFn }}
              className={classNames('w-full flex flex-col flex-1 items-center text-center px-10 overflow-auto', {
                'justify-center': isFullScreenStep,
                'pt-14 pb-8': !isFullScreenStep,
              })}
            />
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {!isFullScreenStep && (
            <MotionDialogActions
              className="flex items-center gap-3 absolute bottom-0 pt-4 inset-x-0 px-10 pb-8"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.5, ease: cubicEasingFn }}
            >
              <SurveyProgress max={StepComponents.length - 2} current={step} />

              <div className="flex-1"></div>

              <DialogButton type="button" onClick={onBack} variant="secondary">
                Back
              </DialogButton>

              <DialogButton type="submit" variant="primary" disabled={!isStepValid}>
                {step === StepComponents.length - 2 ? 'Complete' : 'Next'}
              </DialogButton>
            </MotionDialogActions>
          )}
        </AnimatePresence>
      </form>
    </Dialog>
  );
});

SurveyDialog.displayName = 'SurveyDialog';
